import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode, Html5QrcodeScannerState } from 'html5-qrcode';
import { Callout } from '../components/Callout';

interface Props {
  /** Solo escanea mientras es true; se apaga la camara al ponerlo en false. */
  activo: boolean;
  onLeido: (texto: string) => void;
  /** Tablet/celular: la cámara ocupa la mayor parte de la pantalla. */
  pantallaCompleta?: boolean;
}

/** Toma la ultima parte de la ruta si el QR trae una URL completa. */
export function extraerToken(texto: string): string {
  try {
    const u = new URL(texto);
    const partes = u.pathname.split('/').filter(Boolean);
    return partes[partes.length - 1] || texto.trim();
  } catch {
    return texto.trim();
  }
}

export function EscanerQR({ activo, onLeido, pantallaCompleta }: Props) {
  const idRef = useRef(`qr-reader-${Math.random().toString(36).slice(2)}`);
  const onLeidoRef = useRef(onLeido);
  onLeidoRef.current = onLeido;
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!activo) return;
    setError(null);
    const inst = new Html5Qrcode(idRef.current, { verbose: false });
    let vivo = true;

    function apagar() {
      const estado = inst.getState();
      if (estado !== Html5QrcodeScannerState.SCANNING && estado !== Html5QrcodeScannerState.PAUSED) {
        return;
      }
      inst
        .stop()
        .then(() => inst.clear())
        .catch(() => undefined);
    }

    inst
      .start(
        { facingMode: 'environment' },
        { fps: 10, qrbox: { width: 260, height: 260 } },
        (texto) => vivo && onLeidoRef.current(texto),
        () => undefined,
      )
      .then(() => {
        // Si ya se desmontó/desactivó mientras la cámara terminaba de abrir
        // (p. ej. cambio rápido de pestaña), el cleanup de abajo ya corrió
        // y encontró la cámara aún sin arrancar, así que no la apagó —
        // sin este chequeo se queda prendida para siempre.
        if (!vivo) apagar();
      })
      .catch(() => {
        if (vivo) setError('No se pudo abrir la cámara. Revisa los permisos del navegador.');
      });

    return () => {
      vivo = false;
      apagar();
    };
  }, [activo]);

  return (
    <div className="space-y-3">
      <div
        id={idRef.current}
        className={[
          'mx-auto overflow-hidden rounded-2xl border border-borde bg-navy/5',
          pantallaCompleta
            ? 'aspect-3/4 w-full max-w-none sm:aspect-square'
            : 'aspect-square w-full max-w-sm',
        ].join(' ')}
      />
      {error && <Callout tono="error">{error}</Callout>}
    </div>
  );
}
